import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Box, Typography, TextField, Button, Alert, CircularProgress } from "@mui/material";

/**
 * Landing page for Supabase password-reset and magic-link redirects.
 * Set Supabase Auth → URL Configuration → Redirect URLs to include:
 *   https://<your-domain>/update-password
 * And call resetPasswordForEmail with the same redirectTo.
 */
const UpdatePassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === "PASSWORD_RECOVERY" && session) {
        setReady(true);
        setError("");
        setLoading(false);
      }
    });

    const establishSession = async () => {
      setError("");
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        if (code) {
          const { error: exErr } = await supabase.auth.exchangeCodeForSession(window.location.href);
          if (exErr) {
            if (!cancelled) {
              setError(exErr.message || "Could not verify reset link.");
              setLoading(false);
            }
            return;
          }
        }

        const {
          data: { session },
          error: sessErr,
        } = await supabase.auth.getSession();
        if (sessErr) {
          if (!cancelled) {
            setError(sessErr.message || "Session error.");
            setLoading(false);
          }
          return;
        }
        if (!cancelled) {
          setReady(!!session);
          if (!session) {
            setError("Invalid or expired link. Open “Forgot password?” on the sign-in page and try again.");
          }
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Something went wrong.");
          setLoading(false);
        }
      }
    };

    establishSession();
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      const { error: upErr } = await supabase.auth.updateUser({ password });
      if (upErr) throw upErr;
      setDone(true);
      window.setTimeout(() => navigate("/register?auth=1", { replace: true }), 1500);
    } catch (e2) {
      setError(e2?.message || "Could not update password.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress sx={{ color: "#000" }} />
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={{ maxWidth: 420, mx: "auto", py: 8, px: 2, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Password updated
        </Typography>
        <Typography variant="body2" sx={{ color: "#666" }}>
          Redirecting to sign in…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 420, mx: "auto", py: 6, px: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        Set new password
      </Typography>
      <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
        Choose a new password for your account.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {ready ? (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: "#000", color: "#fff", fontWeight: 700, textTransform: "uppercase" }}
          >
            {saving ? "Saving…" : "Update password"}
          </Button>
        </Box>
      ) : (
        <Typography variant="body2" sx={{ mb: 2 }}>
          <Button component={Link} to="/register?auth=1" variant="outlined" sx={{ borderColor: "#000", color: "#000" }}>
            Back to sign in
          </Button>
        </Typography>
      )}
    </Box>
  );
};

export default UpdatePassword;
